/**
 * Document Hub backend facade (epic FD-3570).
 *
 * Single seam between the UI and the backend. Delegates to the real adapter
 * (`documentHub.realApi.ts`), which is wired to the confirmed contract:
 * presign → PUT to S3 → `POST /documents` → `GET /documents` → `PATCH /documents/:id`.
 * Some endpoints may still be deploying (e.g. `GET /documents`), so consumers
 * handle empty/error states gracefully.
 */
import { CompanyDocument, DocumentExtraction } from './documentHub.schema';
import {
  AddToPackageInput,
  CreatePackageInput,
  DocumentDownloadUrlResponse,
  UpdateDocumentInput,
  UploadBatchResult,
  UploadAttribution,
  UploadBatchStart,
  UploadHandlers,
} from './documentHub.types';
import { realDocumentHubApi } from './documentHub.realApi';

export interface DocumentHubApi {
  /**
   * Upload a batch; returns batch identifiers immediately and a promise for completion.
   * `attribution` is stored on every document in the batch, including the files a ZIP expands into.
   */
  uploadBatch(
    files: File[],
    handlers?: UploadHandlers,
    attribution?: UploadAttribution
  ): { start: UploadBatchStart; done: Promise<UploadBatchResult> };
  /**
   * All documents, newest-first — the Documents table, processing banner, and review flow source.
   * When `companyId` is provided the list is scoped to that company's documents.
   */
  listDocuments(companyId?: number): Promise<CompanyDocument[]>;
  /** One document by id (GET /documents/:id). */
  getDocument(id: number): Promise<CompanyDocument>;
  /** Override AI-detected fields and confirm a document (Review modal → PATCH /documents/:id). */
  updateDocument(id: number, patch: UpdateDocumentInput): Promise<CompanyDocument>;
  /** Group deal docs into one Starbreaker deal-doc package (POST /documents/packages). */
  createPackage(input: CreatePackageInput): Promise<CompanyDocument[]>;
  /** Attach an unpackaged deal doc to an existing package (POST /documents/:id/package). */
  addToPackage(input: AddToPackageInput): Promise<CompanyDocument>;
  /** Detach a deal doc from its package (DELETE /documents/:id/package). */
  removeFromPackage(documentId: number): Promise<CompanyDocument>;
  /** CS-only: push a doc back through AI detection (resets it to detecting). */
  reprocessDocument(id: number): Promise<CompanyDocument>;
  /**
   * Remove a document (DELETE /documents/:id). The BE decides whether the document's
   * state allows it and answers 409 when it does not, so callers surface the refusal
   * rather than predicting it.
   */
  deleteDocument(id: number): Promise<void>;
  /** Presigned URL to download a document's original file (GET /documents/:id/download). */
  getDownloadUrl(id: number): Promise<DocumentDownloadUrlResponse>;
  /** Poll a ZIP fan-out's status by the archive's s3Key; null until the status record exists. */
  getExtraction(s3Key: string): Promise<DocumentExtraction | null>;
}

export const documentHubApi: DocumentHubApi = realDocumentHubApi;
